'use client';

import LandingLayout from '@/components/LandingLayout';
import AnsweringServiceVsGreet from '@/components/AnsweringServiceVsGreet';
import { useAuth } from '@/contexts/AuthContextNext';
import { trackButtonClick, trackPageView } from '@/lib/analytics';
import { PUBLIC_SALE_STARTING_PRICE } from '@/types/pricing';
import { CheckCircleIcon, PhoneIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect } from 'react';

export type QueryMatchLandingProps = {
  pageName: string;
  analyticsContext: string;
  eyebrow: string;
  h1: string;
  subhead: string;
  bullets: string[];
  industryLabel: string;
  industryHref: string;
  industryCtaLabel: string;
  accent?: 'blue' | 'emerald' | 'rose' | 'amber';
  showComparison?: boolean;
  footerHeadline?: string;
  footerSubhead?: string;
};

const accentStyles = {
  blue: {
    hero: 'from-blue-900 via-slate-900 to-blue-900',
    blob: 'from-blue-500/20 to-cyan-500/20',
    badge: 'bg-blue-500/20 border-blue-400/30 text-blue-100',
    badgeDot: 'bg-blue-400',
    primary: 'bg-blue-500 hover:bg-blue-600',
    final: 'from-blue-600 to-cyan-600',
  },
  emerald: {
    hero: 'from-emerald-900 via-green-900 to-emerald-950',
    blob: 'from-green-500/20 to-emerald-500/20',
    badge: 'bg-emerald-500/20 border-emerald-400/30 text-emerald-100',
    badgeDot: 'bg-emerald-400',
    primary: 'bg-emerald-500 hover:bg-emerald-600',
    final: 'from-emerald-600 to-green-600',
  },
  rose: {
    hero: 'from-rose-900 via-pink-950 to-rose-900',
    blob: 'from-rose-500/20 to-pink-500/20',
    badge: 'bg-rose-500/20 border-rose-400/30 text-rose-100',
    badgeDot: 'bg-rose-400',
    primary: 'bg-rose-500 hover:bg-rose-600',
    final: 'from-rose-600 to-pink-600',
  },
  amber: {
    hero: 'from-amber-900 via-orange-950 to-amber-900',
    blob: 'from-amber-500/20 to-orange-500/20',
    badge: 'bg-amber-500/20 border-amber-400/30 text-amber-100',
    badgeDot: 'bg-amber-400',
    primary: 'bg-amber-500 hover:bg-amber-600',
    final: 'from-amber-600 to-orange-600',
  },
};

export default function QueryMatchLanding({
  pageName,
  analyticsContext,
  eyebrow,
  h1,
  subhead,
  bullets,
  industryLabel,
  industryHref,
  industryCtaLabel,
  accent = 'blue',
  showComparison = true,
  footerHeadline = "Don't pay $200–$400/mo for messages",
  footerSubhead = 'Try the AI alternative in under a minute-no signup required.',
}: QueryMatchLandingProps) {
  const { isAuthenticated } = useAuth();
  const styles = accentStyles[accent];
  const comparisonAccent = accent === 'emerald' ? 'emerald' : 'blue';

  useEffect(() => {
    trackPageView(pageName, undefined, { funnel_step: 'query_match_landing' });
  }, [pageName]);

  return (
    <LandingLayout>
      <section className={`relative bg-gradient-to-br ${styles.hero} overflow-hidden`}>
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r ${styles.blob} blur-3xl`} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className={`mb-6 inline-flex items-center gap-3 rounded-full border px-4 py-2 text-sm font-semibold ${styles.badge}`}>
            <span className={`h-2 w-2 rounded-full ${styles.badgeDot}`} />
            {eyebrow}
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {h1}
          </h1>
          <p className="text-xl text-white/90 mb-4 max-w-2xl leading-relaxed">{subhead}</p>
          <p className="text-lg text-white/80 mb-8">
            Flat-rate from {PUBLIC_SALE_STARTING_PRICE} · No per-minute fees · 7-day free trial
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <Link
              href="/try"
              onClick={() => trackButtonClick('try_demo', `${analyticsContext}_hero`)}
              className={`${styles.primary} text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center`}
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Hear it answer in 60 seconds
            </Link>
            <Link
              href={isAuthenticated ? '/app' : '/register'}
              onClick={() =>
                trackButtonClick(isAuthenticated ? 'go_to_dashboard' : 'start_free_trial', `${analyticsContext}_hero`)
              }
              className="bg-white/10 border border-white text-white hover:bg-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-colors text-center"
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Start Free Trial'}
            </Link>
          </div>
          <ul className="space-y-3">
            {bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-3 text-white/90">
                <CheckCircleIcon className="h-6 w-6 text-green-400 shrink-0 mt-0.5" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {showComparison && (
        <AnsweringServiceVsGreet
          industryLabel={industryLabel}
          analyticsContext={`${analyticsContext}_comparison`}
          accent={comparisonAccent}
        />
      )}

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Built for {industryLabel}
          </h2>
          <p className="text-gray-600 mb-8">
            Want the full industry walkthrough? See how Greet works day-to-day for {industryLabel.toLowerCase()}.
          </p>
          <Link
            href={industryHref}
            onClick={() => trackButtonClick('industry_page', analyticsContext)}
            className="text-blue-700 font-semibold hover:text-blue-900"
          >
            {industryCtaLabel} →
          </Link>
        </div>
      </section>

      <section className={`py-20 bg-gradient-to-r ${styles.final}`}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {footerHeadline}
          </h2>
          <p className="text-white/90 text-lg mb-8">
            {footerSubhead}
          </p>
          <Link
            href="/try"
            onClick={() => trackButtonClick('try_demo', `${analyticsContext}_footer`)}
            className="inline-block bg-white text-gray-900 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
          >
            Try it now
          </Link>
        </div>
      </section>
    </LandingLayout>
  );
}
